import { Component } from '@theme/component';

/**
 * Horizontal scroll-snap carousel with previous / next buttons.
 *
 * @typedef {Object} TrainexCarouselRefs
 * @property {HTMLElement} track - Scrollable list of slides
 * @property {HTMLButtonElement} [previous] - Previous button
 * @property {HTMLButtonElement} [next] - Next button
 *
 * @extends {Component<TrainexCarouselRefs>}
 */
class TrainexCarousel extends Component {
  requiredRefs = ['track'];

  connectedCallback() {
    super.connectedCallback();
    this.refs.track.addEventListener('scroll', this.#updateButtons, { passive: true });
    this.#updateButtons();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.refs.track.removeEventListener('scroll', this.#updateButtons);
  }

  previous() {
    this.#scrollBy(-1);
  }

  next() {
    this.#scrollBy(1);
  }

  /** @param {number} direction */
  #scrollBy(direction) {
    const { track } = this.refs;
    const slide = track.firstElementChild;
    if (!(slide instanceof HTMLElement)) return;

    const gap = parseFloat(getComputedStyle(track).columnGap) || 0;
    track.scrollBy({ left: direction * (slide.offsetWidth + gap), behavior: 'smooth' });
  }

  #updateButtons = () => {
    const { track, previous, next } = this.refs;
    const maxScroll = track.scrollWidth - track.clientWidth - 1;

    if (previous) previous.disabled = track.scrollLeft <= 0;
    if (next) next.disabled = track.scrollLeft >= maxScroll;
  };
}

/**
 * Image with clickable points; each point shows its own info panel.
 *
 * @typedef {Object} TrainexHotspotsRefs
 * @property {HTMLButtonElement[]} dots - Hotspot buttons
 * @property {HTMLElement[]} panels - Info panels, same order as dots
 *
 * @extends {Component<TrainexHotspotsRefs>}
 */
class TrainexHotspots extends Component {
  /** @param {number} index */
  select(index) {
    const { dots = [], panels = [] } = this.refs;

    for (const [i, dot] of dots.entries()) {
      dot.setAttribute('aria-expanded', String(i === index));
    }

    for (const [i, panel] of panels.entries()) {
      panel.hidden = i !== index;
    }
  }
}

if (!customElements.get('trainex-carousel-component')) {
  customElements.define('trainex-carousel-component', TrainexCarousel);
}

if (!customElements.get('trainex-hotspots-component')) {
  customElements.define('trainex-hotspots-component', TrainexHotspots);
}
